/**
 * TABLEAU DE BORD — Codes promo « Premier équipement » (KIT-XXXXXX)
 * Suivi : génération, envoi courriel, relances J+7, utilisation, conversion et revenus.
 * Route admin : /app/promos-kit
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Ticket, Mail, BellRing, CheckCircle2, Clock, XCircle, TrendingUp, DollarSign, RefreshCw, Percent, BarChart3 } from "lucide-react";

const STATUS_BADGE = {
  "utilisé": "bg-emerald-900/60 text-emerald-200 border-emerald-700",
  "actif": "bg-blue-900/60 text-blue-200 border-blue-700",
  "expiré": "bg-zinc-800 text-zinc-400 border-zinc-700",
};

function money(n) {
  return (n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " $";
}
function shortDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CA", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return iso.slice(0, 10); }
}

export default function PromoKitDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("tous");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/adhesion/promo-dashboard");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de chargement"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runReminders() {
    setRunning(true);
    try {
      const r = await api.post("/adhesion/run-kit-reminders");
      toast.success(r.data.sent ? `${r.data.sent} relance(s) J+7 envoyée(s)` : "Aucun code à relancer pour l'instant");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setRunning(false);
  }

  if (loading) return <div className="p-6 text-zinc-400" data-testid="kit-loading">Chargement du tableau de bord…</div>;
  const s = data?.stats || {};
  const codes = (data?.codes || []).filter((c) => filter === "tous" || c.status === filter);

  return (
    <div className="p-6 space-y-6" data-testid="promo-kit-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Ticket className="w-8 h-8 text-amber-400" /> Codes promo « Premier équipement »
          </h1>
          <p className="text-zinc-400 mt-1">
            Codes <span className="font-mono text-amber-300">KIT-XXXXXX</span> générés automatiquement après chaque adhésion · ROI du système.
          </p>
        </div>
        <button onClick={runReminders} disabled={running} data-testid="run-reminders-btn"
          className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-black font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2">
          <BellRing className={`w-4 h-4 ${running ? "animate-pulse" : ""}`} /> {running ? "Envoi…" : "Relancer J+7 maintenant"}
        </button>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testid="kpi-generated" icon={Ticket} label="Codes générés" value={s.generated} color="amber" />
        <KPI testid="kpi-used" icon={CheckCircle2} label="Utilisés" value={s.used} sub={`${s.conversion_pct ?? 0}% conversion`} color="emerald" />
        <KPI testid="kpi-revenue" icon={DollarSign} label="Revenu généré" value={money(s.revenue_cad)} raw color="emerald" />
        <KPI testid="kpi-discount" icon={Percent} label="Rabais accordés" value={money(s.discount_given_cad)} raw color="red" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testid="kpi-emailed" icon={Mail} label="Envoyés par courriel" value={s.emailed} color="blue" />
        <KPI testid="kpi-reminders" icon={BellRing} label="Relances J+7" value={s.reminders_sent} color="blue" />
        <KPI testid="kpi-active" icon={Clock} label="Actifs (non utilisés)" value={s.active} color="blue" />
        <KPI testid="kpi-expired" icon={XCircle} label="Expirés" value={s.expired} color="zinc" />
      </div>

      {/* Barre de conversion */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="conversion-bar">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-zinc-400 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Taux de conversion (codes utilisés)</span>
          <span className="font-bold text-emerald-300">{s.conversion_pct ?? 0}% <span className="text-zinc-500 font-normal">/ objectif {s.conversion_goal_pct ?? 30}%</span></span>
        </div>
        <div className="relative h-3 w-full bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, s.conversion_pct || 0)}%` }} />
          <div className="absolute top-0 bottom-0 w-0.5 bg-amber-400" style={{ left: `${Math.min(100, s.conversion_goal_pct || 30)}%` }} title="Objectif" />
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          {s.used} code(s) utilisé(s) sur {s.generated} généré(s) · {money(s.revenue_cad)} de revenu boutique attribué.
          {s.conversion_pct >= (s.conversion_goal_pct || 30)
            ? <span className="text-emerald-400"> · 🎯 Objectif atteint !</span>
            : <span className="text-amber-400"> · {Math.max(0, (s.conversion_goal_pct || 30) - (s.conversion_pct || 0)).toFixed(1)} pts sous l'objectif.</span>}
        </p>
      </div>

      {/* Graphe revenu par mois */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="revenue-chart">
        <div className="flex items-center gap-2 text-zinc-400 text-sm mb-4"><BarChart3 className="w-4 h-4 text-emerald-400" /> Revenu généré par mois (12 derniers mois)</div>
        {(() => {
          const months = data?.by_month || [];
          const maxRev = Math.max(1, ...months.map((m) => m.revenue_cad || 0));
          return (
            <div className="flex items-end gap-1.5 h-40" data-testid="chart-bars">
              {months.map((m) => {
                const h = Math.round(((m.revenue_cad || 0) / maxRev) * 100);
                const [, mm] = m.month.split("-");
                const lbl = ["", "Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"][parseInt(mm)];
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1 group" data-testid={`bar-${m.month}`}>
                    <span className="text-[9px] text-emerald-300 opacity-0 group-hover:opacity-100 transition-opacity">{money(m.revenue_cad)}</span>
                    <div className="w-full bg-emerald-600/70 hover:bg-emerald-500 rounded-t transition-all" style={{ height: `${Math.max(2, h)}%` }} />
                    <span className="text-[9px] text-zinc-500">{lbl}</span>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Filtres + table */}
      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {["tous", "utilisé", "actif", "expiré"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`text-xs px-3 py-1.5 rounded-full border capitalize ${filter === f ? "bg-amber-500 border-amber-400 text-black" : "bg-zinc-900 border-zinc-700 text-zinc-400"}`}>
              {f}
            </button>
          ))}
        </div>

        {codes.length === 0 ? (
          <p className="text-zinc-500 py-8 text-center" data-testid="no-codes">Aucun code pour ce filtre.</p>
        ) : (
          <div className="overflow-x-auto border border-zinc-800 rounded-lg" data-testid="codes-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Membre</th>
                  <th className="text-left px-4 py-3">Rabais</th>
                  <th className="text-left px-4 py-3">Généré</th>
                  <th className="text-left px-4 py-3">Expire</th>
                  <th className="text-center px-4 py-3">Courriel</th>
                  <th className="text-center px-4 py-3">Relance</th>
                  <th className="text-right px-4 py-3">Commande</th>
                  <th className="text-left px-4 py-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`code-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono text-amber-300">{c.code}</td>
                    <td className="px-4 py-3 text-zinc-300">{c.member_email || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400">{c.discount_pct}%</td>
                    <td className="px-4 py-3 text-zinc-400">{shortDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-zinc-400">{shortDate(c.expires)}</td>
                    <td className="px-4 py-3 text-center">{c.emailed ? <Mail className="w-4 h-4 text-blue-400 inline" /> : <span className="text-zinc-600">—</span>}</td>
                    <td className="px-4 py-3 text-center">{c.reminder_sent ? <BellRing className="w-4 h-4 text-amber-400 inline" /> : <span className="text-zinc-600">—</span>}</td>
                    <td className="px-4 py-3 text-right text-emerald-300">{c.order_total_cad != null ? money(c.order_total_cad) : "—"}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_BADGE[c.status] || "bg-zinc-800"}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, testid, raw }) {
  const c = {
    emerald: "text-emerald-400 border-emerald-900",
    blue: "text-blue-400 border-blue-900",
    amber: "text-amber-400 border-amber-900",
    red: "text-red-400 border-red-900",
    zinc: "text-zinc-400 border-zinc-800",
  }[color];
  return (
    <div className={`bg-zinc-900 border rounded-lg p-4 ${c}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider mb-1">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`font-bold ${raw ? "text-2xl" : "text-3xl"} text-zinc-100`}>{value ?? 0}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
